import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import {
  TrendingUp, Package, Users, Receipt, AlertTriangle, Wallet,
  AlertOctagon, AlertCircle, CheckCircle2, UserCheck, IndianRupee, Clock,
} from "lucide-react";

const Stat = ({ label, value, icon: Icon, accent, testid }) => (
  <Card className="rounded-sm border-slate-200 p-6" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</div>
        <div className="mt-3 text-3xl font-bold tracking-tight text-slate-900 tabular-nums" style={{ fontFamily: "Cabinet Grotesk, IBM Plex Sans, sans-serif" }}>
          {value}
        </div>
      </div>
      <div className={`p-2 ${accent}`}><Icon className="w-5 h-5" strokeWidth={1.75} /></div>
    </div>
  </Card>
);

const SEVERITY = {
  critical: { Icon: AlertOctagon, bg: "bg-red-50 border-red-200", text: "text-red-700" },
  warning: { Icon: AlertCircle, bg: "bg-amber-50 border-amber-200", text: "text-amber-700" },
  positive: { Icon: CheckCircle2, bg: "bg-green-50 border-green-200", text: "text-green-700" },
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState({ critical: [], warning: [], positive: [] });
  const [payroll, setPayroll] = useState(null);
  useEffect(() => {
    api.get("/bi/overview").then((r) => setStats(r.data)).catch(() => {});
    api.get("/alerts").then((r) => setAlerts(r.data)).catch(() => {});
    api.get("/staff/kpi/payroll").then((r) => setPayroll(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="text-slate-500" data-testid="dashboard-loading">Loading...</div>;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Overview</div>
        <h2 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Cabinet Grotesk, IBM Plex Sans, sans-serif" }}>
          Hello, {user.name.split(" ")[0]}.
        </h2>
        <p className="text-slate-500 mt-1">Today's retail pulse.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat testid="stat-rev-today" label="Revenue Today" value={formatINR(stats.revenue_today)} icon={TrendingUp} accent="bg-green-50 text-green-700" />
        <Stat testid="stat-rev-week" label="Revenue · 7 days" value={formatINR(stats.revenue_week)} icon={TrendingUp} accent="bg-emerald-50 text-emerald-700" />
        <Stat testid="stat-bills-today" label="Bills Today" value={stats.bills_today} icon={Receipt} accent="bg-blue-50 text-blue-700" />
        <Stat testid="stat-products" label="Products" value={stats.total_products} icon={Package} accent="bg-slate-100 text-slate-700" />
        <Stat testid="stat-customers" label="Customers" value={stats.total_customers} icon={Users} accent="bg-slate-100 text-slate-700" />
        <Stat testid="stat-low-stock" label="Low Stock" value={stats.low_stock_count} icon={AlertTriangle} accent="bg-amber-50 text-amber-700" />
        <Stat testid="stat-expenses" label="Expenses Total" value={formatINR(stats.expenses_total)} icon={Wallet} accent="bg-rose-50 text-rose-700" />
        <Stat testid="stat-rev-total" label="Revenue All-Time" value={formatINR(stats.revenue_total)} icon={TrendingUp} accent="bg-slate-100 text-slate-700" />
        <Stat testid="stat-bills-total" label="Total Bills" value={stats.total_bills} icon={Receipt} accent="bg-slate-100 text-slate-700" />
      </div>

      {stats.per_branch && stats.per_branch.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">Per Branch Performance</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {stats.per_branch.map((b) => (
              <Card key={b.branch_id} className="rounded-sm border-slate-200 p-5" data-testid={`branch-stat-${b.branch_id}`}>
                <div className="text-sm font-semibold text-slate-900">{b.branch_name}</div>
                <div className="text-2xl font-bold tracking-tight text-slate-900 mt-2 tabular-nums">{formatINR(b.revenue)}</div>
                <div className="text-xs text-slate-500 mt-1">{b.count} bills</div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {(alerts.critical.length + alerts.warning.length + alerts.positive.length) > 0 && (
        <div data-testid="alerts-panel">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">Alerts</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {["critical", "warning", "positive"].map((sev) => alerts[sev].map((a, i) => {
              const { Icon, bg, text } = SEVERITY[sev];
              return (
                <div key={`${sev}-${i}`} className={`border ${bg} p-4 flex items-start gap-3`} data-testid={`alert-${sev}-${a.kind}`}>
                  <Icon className={`w-5 h-5 ${text} flex-shrink-0 mt-0.5`} />
                  <div className="text-sm">
                    <div className={`font-semibold ${text}`}>{a.message}</div>
                    {a.items && a.items.length > 0 && (
                      <ul className="text-xs text-slate-600 mt-1 list-disc list-inside">
                        {a.items.slice(0, 3).map((it) => <li key={it.id}>{it.name}{it.stock !== undefined ? ` (${it.stock})` : ""}</li>)}
                      </ul>
                    )}
                  </div>
                </div>
              );
            }))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link to="/bills" className="block group" data-testid="quick-bills">
          <Card className="rounded-sm border-slate-200 p-6 transition-all group-hover:shadow-md group-hover:border-slate-900">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-slate-900 text-white"><Receipt className="w-6 h-6" /></div>
              <div>
                <div className="font-semibold text-slate-900">View Recent Bills</div>
                <div className="text-sm text-slate-500">Sales history, reprint invoices, WhatsApp customers.</div>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/staff" className="block group" data-testid="quick-staff">
          <Card className="rounded-sm border-slate-200 p-6 transition-all group-hover:shadow-md group-hover:border-slate-900">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-slate-100 text-slate-900"><UserCheck className="w-6 h-6" /></div>
              <div>
                <div className="font-semibold text-slate-900">Staff & Attendance</div>
                <div className="text-sm text-slate-500">Mark attendance, disperse salary, track payroll.</div>
              </div>
            </div>
          </Card>
        </Link>
      </div>

      {payroll && (
        <div data-testid="payroll-kpi-section">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">Staff & Payroll — {payroll.month}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Stat testid="kpi-salary-dispersed" label="Salary Dispersed" value={formatINR(payroll.dispersed_total_this_month)} icon={IndianRupee} accent="bg-green-50 text-green-700" />
            <Stat testid="kpi-salary-pending" label="Pending Dispersement" value={formatINR(payroll.pending_total_this_month)} icon={Clock} accent="bg-amber-50 text-amber-700" />
            <Stat testid="kpi-staff-count" label="Active Staff" value={payroll.active_staff_count} icon={UserCheck} accent="bg-slate-100 text-slate-700" />
            <Stat testid="kpi-attendance-rate" label="Attendance %" value={`${payroll.attendance_rate_pct}%`} icon={CheckCircle2} accent="bg-blue-50 text-blue-700" />
            <Stat testid="kpi-total-staff" label="Total Staff" value={payroll.total_staff_count} icon={Users} accent="bg-slate-100 text-slate-700" />
          </div>
          {payroll.per_branch_salary_cost?.length > 0 && (
            <div className="mt-4 border border-slate-200 bg-white p-4">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Branch-wise Salary Cost (monthly budget)</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {payroll.per_branch_salary_cost.map((b) => (
                  <div key={b.branch_id} className="border border-slate-200 p-3" data-testid={`branch-payroll-${b.branch_id}`}>
                    <div className="text-sm font-medium">{b.branch_name}</div>
                    <div className="text-lg font-bold tabular-nums">{formatINR(b.salary_cost)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
